import dayjs from 'dayjs';

/**
 * Period shortcuts, grouped the way people actually ask for a period: a rolling window, a calendar
 * period, or the financial year. The From/To pickers sit beside them and stay editable, so any range
 * that is not on this list is still one click away — a preset is a shortcut, never the only way in.
 *
 * The Indian financial year runs April–March, which is why "This FY" is not just "this year".
 */
const d = (x: dayjs.Dayjs) => x.format('YYYY-MM-DD');

const fyStart = (offsetYears = 0) => {
  const now = dayjs().add(offsetYears, 'year');
  return now.month() >= 3 ? now.month(3).startOf('month') : now.subtract(1, 'year').month(3).startOf('month');
};

export interface Preset { label: string; group: string; range: () => [string, string] }

export const PRESET_GROUPS = ['Rolling', 'Calendar', 'Financial year'] as const;

export const PRESETS: Preset[] = [
  { label: 'Today', group: 'Rolling', range: () => [d(dayjs()), d(dayjs())] },
  { label: 'Yesterday', group: 'Rolling', range: () => [d(dayjs().subtract(1, 'day')), d(dayjs().subtract(1, 'day'))] },
  { label: 'Last 7 days', group: 'Rolling', range: () => [d(dayjs().subtract(6, 'day')), d(dayjs())] },
  { label: 'Last 30 days', group: 'Rolling', range: () => [d(dayjs().subtract(29, 'day')), d(dayjs())] },
  { label: 'Last 90 days', group: 'Rolling', range: () => [d(dayjs().subtract(89, 'day')), d(dayjs())] },

  { label: 'This week', group: 'Calendar', range: () => [d(dayjs().startOf('week')), d(dayjs())] },
  { label: 'Last week', group: 'Calendar', range: () => [d(dayjs().subtract(1, 'week').startOf('week')), d(dayjs().subtract(1, 'week').endOf('week'))] },
  { label: 'This month', group: 'Calendar', range: () => [d(dayjs().startOf('month')), d(dayjs())] },
  { label: 'Last month', group: 'Calendar', range: () => [d(dayjs().subtract(1, 'month').startOf('month')), d(dayjs().subtract(1, 'month').endOf('month'))] },
  { label: 'This quarter', group: 'Calendar', range: () => [d(dayjs().startOf('month').subtract(dayjs().month() % 3, 'month')), d(dayjs())] },
  { label: 'Last quarter', group: 'Calendar', range: () => {
    const qStart = dayjs().startOf('month').subtract(dayjs().month() % 3, 'month');
    return [d(qStart.subtract(3, 'month')), d(qStart.subtract(1, 'day'))];
  } },
  { label: 'This year', group: 'Calendar', range: () => [d(dayjs().startOf('year')), d(dayjs())] },

  { label: 'This FY', group: 'Financial year', range: () => [d(fyStart()), d(dayjs())] },
  { label: 'Last FY', group: 'Financial year', range: () => [d(fyStart(-1)), d(fyStart().subtract(1, 'day'))] },
];

/** Which shortcut a range IS, so a control can say "Last 30 days" instead of two dates to compare. */
export const presetFor = (from: string, to: string): string | undefined =>
  PRESETS.find((p) => { const [pf, pt] = p.range(); return pf === from && pt === to; })?.label;
